import React from "react";
import { Stack, Box, Typography } from "@mui/material";
import { useScrollToSection } from "~/single-page/hooks/useScrollToSection";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

const scrollMarginStyle = {
  scrollMarginTop: '100px'
};

export default function IncomeTaxPage() {
  useScrollToSection();

  return (
    <Stack spacing={4} sx={{ maxWidth: '100%' }}>
      <Box id="how-to-donate" sx={scrollMarginStyle}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2, mt: 2, textAlign: 'center' }}>
          Doe parte do seu Imposto de Renda
        </Typography>
        <Box 
          sx={{ 
            mb: 3,
            mt: 2
          }}
        >
          <a 
            href="https://declareseubairrismo.recife.pe.gov.br/" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              display: 'block',
              transition: 'transform 0.3s ease',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <img 
              src="/images/banner_ir.jpg" 
              alt="Declare seu Bairrismo - Doe parte do seu Imposto de Renda" 
              style={{ 
                width: '100%', 
                height: 'auto',
                display: 'block',
              }}
            />
          </a>
          <Typography 
            variant="body2" 
            sx={{ 
              textAlign: 'center', 
              mt: 1, 
              color: 'text.secondary', 
              fontSize: '0.85rem' 
            }}
          >
            Clique no banner para saber como doar parte do seu IR para projetos sociais
          </Typography>
        </Box>
        <Typography variant="body1">
          O seu imposto devido pode ter destinação especial. Esse dinheiro pode ajudar crianças, adolescentes e idosos
          em situação de vulnerabilidade social. Essa turma está pronta para receber a sua doação por meio de dois fundos
          específicos e responsáveis por distribuir os recursos doados a instituições impactam positivamente a vida de
          muita gente.
        </Typography>
      </Box>

      <Box>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
          Como funciona?
        </Typography>
        <Box sx={{ 
          width: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mb: 2,
          flexDirection: 'column'
        }}>
          <Box sx={{
            width: '100%',
            maxWidth: '850px',
            position: 'relative',
            paddingBottom: '56.25%',
            height: 0,
            overflow: 'hidden',
            '& iframe': {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              borderRadius: '8px'
            }
          }}>
            <iframe 
              width="560" 
              height="315" 
              src="https://www.youtube.com/embed/46GSvqlVQUw?si=_TV6zTlZ6cSRGxlh" 
              title="YouTube video player" 
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
              referrerPolicy="strict-origin-when-cross-origin" 
              allowFullScreen
            ></iframe>
          </Box>
        </Box>
      </Box>

      {/* Conheça os Fundos */}
      <Box id="to-whom-to-donate" sx={scrollMarginStyle}>
        <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
          Conheça os Fundos
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          gap: 4, 
          justifyContent: 'center', 
          flexDirection: { xs: 'column', md: 'row' }
        }}>
          {/* Card do Fundo da Criança */}
          <Box sx={{ 
            flex: 1, 
            maxWidth: { xs: '100%', md: '600px' }
          }}>
            <Card sx={{ 
              height: '100%',
              borderRadius: '40px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden',
              pb: 11,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-100px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '180px',
                height: '180px',
                backgroundColor: '#009FE3',
                borderRadius: '50%',
                zIndex: 1
              }
            }}>
              <CardContent sx={{ 
                flex: 1, 
                px: 4,
                pt: 4,
                pb: 3
              }}>
                <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Fundo da Criança e do Adolescente
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Apoie projetos que promovem a proteção e o desenvolvimento de crianças e adolescentes em situação de vulnerabilidade. 
                  Sua doação contribui para programas de educação, saúde e assistência social.
                </Typography>
                <Typography
                  component="a"
                  href="#como-doar-crianca"
                  sx={{ 
                    color: '#009FE3',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    },
                    fontWeight: 'bold'
                  }}
                >
                  Clique aqui e saiba como doar
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Card do Fundo do Idoso */}
          <Box sx={{ 
            flex: 1, 
            maxWidth: { xs: '100%', md: '600px' }
          }}>
            <Card sx={{ 
              height: '100%',
              borderRadius: '40px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden',
              pt: 11,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-100px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '180px',
                height: '180px',
                backgroundColor: '#009FE3',
                borderRadius: '50%',
                zIndex: 1
              }
            }}>
              <CardContent sx={{ 
                flex: 1, 
                px: 4,
                pt: 4,
                pb: 3
              }}>
                <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Fundo da Pessoa Idosa
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Contribua com iniciativas que garantem qualidade de vida e dignidade aos idosos. 
                  Os recursos são destinados a projetos de acolhimento, saúde e atividades sociais.
                </Typography>
                <Typography
                  component="a"
                  href="#como-doar-idoso"
                  sx={{ 
                    color: '#009FE3',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    },
                    fontWeight: 'bold'
                  }}
                >
                  Clique aqui e saiba como doar
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Como doar pela declaração */}
      <Box id="donate-in-the-declaration" sx={scrollMarginStyle}>
        <Card sx={{ borderRadius: '40px' }}>
          <CardContent sx={{ 
            px: 4,
            pt: 4,
            pb: 3
          }}>
            <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
              Como doar pela Declaração do IR
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              É possível destinar até 6% do seu Imposto de Renda devido para os fundos sociais. 
              O processo é simples e não representa nenhum custo adicional, pois é deduzido do imposto que você já pagaria.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              A doação pode ser feita diretamente na declaração do IR, sem complicações e com total segurança.
            </Typography>
            <Typography
              component="a"
              href="#como-doar-idoso"
              sx={{ 
                color: '#009FE3',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                },
                fontWeight: 'bold'
              }}
            >
              Clique aqui e saiba como doar
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Stack>
  );
}
